/**
 * Creating an asset.
 *
 * `POST /api/assets` has been audited and tested since 2026-08-20 and no screen
 * called it. The asset Edit tab (2026-08-30) closed the other half — the product
 * could change an asset and still not be told one existed, which is the sentence
 * cosmon-next's own README used to describe its worst gap.
 *
 * ── Why this is a button on the fleet and not a fleet redesign ───────────
 *
 * ROADMAP §6 has an open owner decision — *"Fleet default view at ~347 cameras:
 * faults-first single surface, or site roll-ups?"* — and it gates M3's Fleet
 * work. This is deliberately not that: it adds one affordance above the grid and
 * changes nothing about how the grid is arranged, so it does not need the answer
 * and does not prejudge it.
 *
 * ── The refusals are the server's ────────────────────────────────────────
 *
 * Same position as the Edit tab. An active asset with no address, a driver that
 * cannot answer for the kind, a code that collides case-insensitively, a site in
 * another tenant — every one of those is refused by `validateAssetEdit` and
 * `checkAssetReferences`, and shown here as the sentence they returned. The one
 * check made locally is JSON that will not parse, because that never becomes a
 * request and so has no server sentence to show.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Drawer } from './components/index.ts';
import {
  ADDRESS_KEYS,
  ConfigTextError,
  LIFECYCLES,
  PUSH_ONLY_DRIVERS,
  bodyFrom,
  type Draft,
} from './asset-form.ts';

interface SiteOption {
  id: string;
  code: string | null;
  name: string;
}
interface CredentialOption {
  id: string;
  label: string;
  kind: string;
}
interface KindOption {
  kind: string;
  label: string;
}
interface DriverOption {
  id: string;
  asset_kind: string;
  label: string;
}

/**
 * A blank asset.
 *
 * ⚠️ `lifecycle` starts at `provisioning` and not at `active`, matching
 * `validateAssetEdit`'s own default. A form that defaulted to active would
 * refuse to submit until an address was typed — correct, but it would teach
 * people that the address box is mandatory when the real rule is that ACTIVE
 * needs one. Provisioning first is also the real workflow: create it, then fill
 * in what the installer sends through.
 */
const EMPTY: Draft = {
  name: '',
  code: '',
  kind: '',
  driverId: '',
  siteId: '',
  credentialId: '',
  lifecycle: 'provisioning',
  notes: '',
  addressKey: 'host',
  address: '',
  port: '',
  extraConfig: '',
};

export function NewAsset({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [credentials, setCredentials] = useState<CredentialOption[]>([]);
  const [kinds, setKinds] = useState<KindOption[]>([]);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    void api
      .get<{ sites: SiteOption[] }>('/api/sites')
      .then((r) => setSites(r.sites))
      .catch(() => setSites([]));
    void api
      .get<{ credentials: CredentialOption[] }>('/api/credentials')
      .then((r) => setCredentials(r.credentials))
      .catch(() => setCredentials([]));
    void api
      .get<{ kinds: KindOption[]; drivers: DriverOption[] }>('/api/catalogue')
      .then((r) => {
        setKinds(r.kinds);
        setDrivers(r.drivers);
        // Pick the first kind rather than leaving the select on nothing: `kind`
        // is required, and an empty one is refused with a message about a
        // catalogue the person is looking at.
        setDraft((d) => (d.kind === '' && r.kinds[0] ? { ...d, kind: r.kinds[0].kind } : d));
      })
      .catch(() => {
        setKinds([]);
        setDrivers([]);
      });
  }, [open]);

  const edit = useCallback((patch: Partial<Draft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const create = useCallback(async () => {
    let body: Record<string, unknown>;
    try {
      body = bodyFrom(draft);
    } catch (err) {
      setError(err instanceof ConfigTextError ? err.message : String(err));
      return;
    }

    setBusy(true);
    setNotice(null);
    try {
      const result = await api.post<{ code?: string; name?: string; unrecognisedConfig?: string[] }>(
        '/api/assets',
        body,
      );
      const unknown = result.unrecognisedConfig ?? [];
      setNotice(
        `Created ${result.code ?? result.name ?? draft.name}.` +
          (unknown.length === 0
            ? ''
            : ` Kept ${unknown.length} config key(s) nothing recognises: ${unknown.join(', ')}.`),
      );
      setError(null);
      setDraft({ ...EMPTY, kind: draft.kind, siteId: draft.siteId });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [draft, onCreated]);

  const mismatched = drivers.find((d) => d.id === draft.driverId && d.asset_kind !== draft.kind);

  return (
    <Drawer open={open} onClose={onClose} title="New asset">
      {error !== null && <Banner tone="fault">{error}</Banner>}
      {notice !== null && (
        <p className="banner" role="status">
          {notice}
        </p>
      )}

      <div className="field">
        <label htmlFor="new-asset-name">Name</label>
        <input
          id="new-asset-name"
          value={draft.name}
          onChange={(e) => edit({ name: e.target.value })}
          disabled={busy}
        />
      </div>

      <div className="field">
        <label htmlFor="new-asset-code">Code</label>
        <input
          id="new-asset-code"
          value={draft.code}
          onChange={(e) => edit({ code: e.target.value })}
          disabled={busy}
        />
        <p className="small muted">
          Compared case-insensitively, so CK1048 and ck1048 cannot become two assets.
        </p>
      </div>

      <div className="field">
        <label htmlFor="new-asset-kind">Kind</label>
        <select
          id="new-asset-kind"
          value={draft.kind}
          onChange={(e) => edit({ kind: e.target.value })}
          disabled={busy}
        >
          {kinds.map((k) => (
            <option key={k.kind} value={k.kind}>
              {k.label} ({k.kind})
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="new-asset-driver">Driver</label>
        <select
          id="new-asset-driver"
          value={draft.driverId}
          onChange={(e) => edit({ driverId: e.target.value })}
          disabled={busy}
        >
          <option value="">— none —</option>
          {drivers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.id} — {d.label}
              {d.asset_kind === draft.kind ? '' : ` · for a ${d.asset_kind}`}
            </option>
          ))}
        </select>
        {mismatched !== undefined && (
          <p className="small muted">
            {mismatched.id} is for a {mismatched.asset_kind}, not a {draft.kind}. The server will
            refuse this — every check on the asset would resolve unavailable.
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor="new-asset-site">Site</label>
        <select
          id="new-asset-site"
          value={draft.siteId}
          onChange={(e) => edit({ siteId: e.target.value })}
          disabled={busy}
        >
          <option value="">— none —</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code === null ? s.name : `${s.code} — ${s.name}`}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="new-asset-lifecycle">Lifecycle</label>
        <select
          id="new-asset-lifecycle"
          value={draft.lifecycle}
          onChange={(e) => edit({ lifecycle: e.target.value })}
          disabled={busy}
        >
          {LIFECYCLES.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
        {draft.lifecycle === 'active' && draft.address.trim() === '' && !PUSH_ONLY_DRIVERS.includes(draft.driverId) && (
          <p className="small muted">
            An active asset needs an address, or the collector cannot dial it and it records
            nothing while appearing on the fleet. Leave it provisioning until you have one.
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor="new-asset-credential">Credential</label>
        <select
          id="new-asset-credential"
          value={draft.credentialId}
          onChange={(e) => edit({ credentialId: e.target.value })}
          disabled={busy}
        >
          <option value="">— none —</option>
          {credentials.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label} ({c.kind})
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="new-asset-address">Address</label>
        <span className="row">
          <select
            aria-label="Address key"
            value={draft.addressKey}
            onChange={(e) => edit({ addressKey: e.target.value })}
            disabled={busy}
          >
            {ADDRESS_KEYS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <input
            id="new-asset-address"
            value={draft.address}
            onChange={(e) => edit({ address: e.target.value })}
            disabled={busy}
          />
        </span>
      </div>

      <div className="field">
        <label htmlFor="new-asset-port">Port</label>
        <input
          id="new-asset-port"
          value={draft.port}
          onChange={(e) => edit({ port: e.target.value })}
          disabled={busy}
          placeholder="80"
        />
      </div>

      <div className="row">
        <Button variant="primary" busy={busy} onClick={() => void create()}>
          Create
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
      </div>
    </Drawer>
  );
}
